import type { TFunction } from 'i18next';

/**
 * Wording for a country's subdivision type. The database stores a stable code, never a label —
 * a label in the database is not translatable — so the display lives here.
 *
 * A `switch` of string literals rather than a key built as 'geo.' + code: the repo's static guard
 * in `src/i18n-jira-integrity.test.ts` requires every translation call to take a literal, and a
 * concatenated key is invisible to the parity check. Plurals use `_one` / `_other` (i18next v25).
 */
export const subdivisionLabel = (t: TFunction, code: string, count = 1): string => {
  switch (code) {
    case 'department':
      return t('Department', { count });
    case 'province':
      return t('Province', { count });
    case 'county':
      return t('County', { count });
    default:
      // An unknown code means the CHECK constraint gained a value the UI has not learned yet.
      // Falling back keeps the screen readable instead of printing a raw code.
      return t('Subdivision', { count });
  }
};

/**
 * The "all" word a filter shows when nothing is picked, gendered per subdivision type: French
 * says "Tous" of départements but "Toutes" of provinces, so one shared key would read wrong.
 */
export const allSubdivisionsLabel = (t: TFunction, code: string): string => {
  switch (code) {
    case 'department':
      return t('All departments');
    case 'province':
      return t('All provinces');
    case 'county':
      return t('All counties');
    default:
      return t('All subdivisions');
  }
};
